import React from "react";
import { SHEET_VELLUM, sheetGridStyle } from "../ui/SheetGround";

/* The folio vocabulary - the project's drawing-sheet design language.

   One set of primitives (graph ground, dimension lines, title block, sheet
   heads) parameterised by a theme. This began as the 08a Folio design study
   and is now the language the live site is drawn in, so it lives in shared/
   rather than under a landing design folder: the app chrome reads from here
   too, and a study is not a sound place for the header to get its palette.

   The study files under landing/designs/drafting still import "./kit", which
   forwards here, so there is exactly one definition of the vocabulary. */

export interface DraftTheme {
  ground: string;
  gridMinor: string;
  gridMajor: string;
  panel: string;
  ink: string;
  mute: string;
  accent: string;
  accentDeep: string;
  rule: string;
  onAccent: string;
  placeholder: string;
}

/* The ground's three colours come from SheetGround, which draws them; the
   theme adds the ink the vocabulary is written in. Spread rather than
   restated, so there is one definition of the sheet in the project. */
export const VELLUM: DraftTheme = {
  ...SHEET_VELLUM,
  panel: "#EDF0EE",
  ink: "#15201D",
  mute: "#5A6A66",
  accent: "#0A6E7E",
  accentDeep: "#085A69",
  rule: "#B4C4C0",
  onAccent: "#EDF0EE",
  placeholder: "#7E8D89",
};

export const BLUEPRINT: DraftTheme = {
  ground: "#0E2E52",
  gridMinor: "#194070",
  gridMajor: "#2A5891",
  panel: "#123A66",
  ink: "#EAF2FA",
  mute: "#A8C4E0",
  accent: "#7FD4E8",
  accentDeep: "#A6E4F2",
  rule: "#2F5F92",
  onAccent: "#0E2E52",
  placeholder: "#7E9CBF",
};

export const SANS = "'Familjen Grotesk', 'Helvetica Neue', Helvetica, Arial, sans-serif";
export const MONO = "'Roboto Mono', ui-monospace, Menlo, monospace";

/* 10mm minor / 50mm major graph ground, drawn rather than imaged. The ruling
   itself lives in shared/ui/SheetGround, which is the one place the project
   defines it; this is a forward, not a copy.

   Returns both properties together, because the image is meaningless without
   its tile size - one period per tile is what keeps the ruling even. Prefer
   the SheetGround component; this is for surfaces that need the ground on an
   element they already own. */
export const gridBackground = (t: DraftTheme, minor?: number, major?: number): React.CSSProperties =>
  sheetGridStyle(t, minor, major);

export const display = (t: DraftTheme, size: string, color?: string): React.CSSProperties => ({
  fontFamily: SANS,
  fontWeight: 700,
  fontSize: size,
  lineHeight: 1.0,
  letterSpacing: "-0.03em",
  color: color || t.ink,
  margin: 0,
});

export const mono = (t: DraftTheme, color?: string): React.CSSProperties => ({
  fontFamily: MONO,
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color || t.mute,
});

export const body = (t: DraftTheme, color?: string): React.CSSProperties => ({
  fontFamily: SANS,
  fontSize: 16.5,
  lineHeight: 1.62,
  color: color || t.mute,
  margin: 0,
});

export const btn = (t: DraftTheme, kind: "accent" | "outline" | "invert"): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "14px 24px",
  background: kind === "accent" ? t.accent : kind === "invert" ? t.ink : "transparent",
  color: kind === "accent" ? t.onAccent : kind === "invert" ? t.ground : t.ink,
  border: kind === "outline" ? `1.5px solid ${t.ink}` : "none",
  borderRadius: 0,
  fontFamily: MONO,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
});

export const input = (t: DraftTheme): React.CSSProperties => ({
  flex: 1,
  minWidth: 0,
  background: "transparent",
  border: "none",
  outline: "none",
  padding: "14px 16px",
  fontFamily: SANS,
  fontSize: 16,
  color: t.ink,
});

/* Dimension line - extension ticks, rule, measurement in the middle. */
export const DimLine: React.FC<{ t: DraftTheme; label: string; vertical?: boolean }> = ({ t, label, vertical }) =>
  vertical ? (
    <div aria-hidden style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%" }}>
      <span style={{ height: 1, width: 13, background: t.accent }} />
      <span style={{ flex: 1, width: 1, background: t.accent }} />
      <span style={{ ...mono(t, t.accent), fontSize: 9, writingMode: "vertical-rl", letterSpacing: "0.12em" }}>{label}</span>
      <span style={{ flex: 1, width: 1, background: t.accent }} />
      <span style={{ height: 1, width: 13, background: t.accent }} />
    </div>
  ) : (
    <div aria-hidden style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ width: 1, height: 13, background: t.accent }} />
      <span style={{ flex: 1, height: 1, background: t.accent }} />
      <span style={{ ...mono(t, t.accent), fontSize: 9.5, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: t.accent }} />
      <span style={{ width: 1, height: 13, background: t.accent }} />
    </div>
  );

export const SheetHead: React.FC<{ t: DraftTheme; code?: string; size?: string; children: React.ReactNode }> = ({
  t,
  code,
  size = "clamp(28px, 3.8vw, 46px)",
  children,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: 24,
      borderBottom: `1.5px solid ${t.ink}`,
      paddingBottom: 12,
      marginBottom: 30,
      flexWrap: "wrap",
    }}
  >
    <h2 style={display(t, size)}>{children}</h2>
    {code && <span style={mono(t, t.accent)}>{code}</span>}
  </div>
);

/* The corner block of a drawing sheet. */
export const TitleBlock: React.FC<{ t: DraftTheme; rows: [string, string][] }> = ({ t, rows }) => (
  <div style={{ border: `1.5px solid ${t.ink}` }}>
    {rows.map(([k, v], i) => (
      <div key={k} style={{ display: "grid", gridTemplateColumns: "104px minmax(0,1fr)", borderTop: i === 0 ? "none" : `1px solid ${t.rule}` }}>
        <span style={{ ...mono(t), padding: "10px 12px", borderRight: `1px solid ${t.rule}` }}>{k}</span>
        <span style={{ fontFamily: MONO, fontSize: 13, color: t.ink, padding: "10px 12px" }}>{v}</span>
      </div>
    ))}
  </div>
);

/* Callout bubble used to key a note to the drawing. */
export const Callout: React.FC<{ t: DraftTheme; children: React.ReactNode }> = ({ t, children }) => (
  <span
    style={{
      ...mono(t, t.accent),
      border: `1px solid ${t.accent}`,
      width: 22,
      height: 22,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 999,
      fontSize: 9.5,
      letterSpacing: 0,
      flexShrink: 0,
    }}
  >
    {children}
  </span>
);

/* Interaction styles, emitted once per surface with a class prefix.

   Note on the placeholder: the theme's `placeholder` (#7E8D89) measures
   3.02:1 against the ground and fails WCAG AA 1.4.3. It is kept here so the
   08a study renders exactly as approved; live surfaces override it after
   this block with `mute`, which measures 4.96:1. */
export const draftCss = (t: DraftTheme, p: string) => `
  .${p}-input::placeholder { color: ${t.placeholder}; }
  .${p}-link:hover { color: ${t.accent}; }
  .${p}-accent:hover { background: ${t.accentDeep}; }
  .${p}-out:hover { background: ${t.ink}; color: ${t.ground}; }
  .${p}-invert:hover { background: ${t.accent}; color: ${t.onAccent}; }
  .${p}-ghost:hover { background: ${t.accent}; color: ${t.onAccent}; }
  .${p}-entry { transition: border-color 160ms ease, background 160ms ease; }
  .${p}-entry:hover { border-top-color: ${t.accent}; }
  .${p}-entry:hover .${p}-entry-title { color: ${t.accent}; }
  .${p}-accent:focus-visible, .${p}-out:focus-visible, .${p}-invert:focus-visible,
  .${p}-ghost:focus-visible, .${p}-entry:focus-visible {
    outline: 2px solid ${t.accent}; outline-offset: 3px;
  }
`;
